import {
    useMutation,
    useQueryClient,
    type UseMutateFunction,
} from '@tanstack/react-query';

import { toast, toastError } from '@/components/ui/toast';
import { useTokenPairStore } from '@/features/tokenPairs';
import { isTradingAuthorityRequiredError } from '@/features/tradingSession/clientAuthority';
import { useSpeedBackend } from '@/features/wallet';
import type {
    UpdateTriggersRequest,
    UpdateTriggersResponse,
} from '@/lib/api/speed';
import { queryKeys } from '@/lib/query';

const UPDATE_TRIGGERS_ERROR_TOAST_MS = 10_000;

interface UseUpdateTriggersResult {
    /**
     * Fires `POST /speed/trade/update-triggers`. Tri-state per leg — see
     * {@link UpdateTriggersRequest}. Per-call callbacks are still accepted (the
     * caller collapses its editor from `onSuccess`); they run in addition to the
     * toasts/invalidation below.
     */
    mutate: UseMutateFunction<
        UpdateTriggersResponse,
        Error,
        UpdateTriggersRequest
    >;
    /** True while the (long-running, agent-signing) POST is in flight. */
    isPending: boolean;
}

/**
 * Edits the TP/SL of the user's already-open trade from the live-position view.
 *
 * The sibling `useEnsureTriggers` repairs missing legs silently in the
 * background; this one is the opposite — it is only ever called from an explicit
 * user action, so BOTH outcomes toast. On success the active + history trades
 * queries are invalidated so the position readout re-renders from the
 * backend's post-rounding values rather than from what was typed.
 *
 * The endpoint is rate-limited 6/min server-side; the caller must keep its Apply
 * control disabled while `isPending` instead of retrying on its own.
 */
export function useUpdateTriggers(): UseUpdateTriggersResult {
    const backend = useSpeedBackend();
    const queryClient = useQueryClient();
    const networkId = useTokenPairStore((s) => s.networkId);

    const { mutate, isPending } = useMutation({
        mutationFn: async (body: UpdateTriggersRequest) => {
            if (!backend) {
                throw new Error('Not authenticated.');
            }
            return backend.updateTriggers(body);
        },
        onSuccess: () => {
            // refetch so the displayed TP/SL come from the trade row the backend
            // actually wrote (tick-rounded), not from the draft that was sent;
            // history too, in case the edit raced a leg that already fired.
            void queryClient.invalidateQueries({
                queryKey: queryKeys.trades.active(networkId ?? 0),
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.trades.history(networkId ?? 0),
            });
            toast.success('Stop-loss / take-profit updated.');
        },
        onError: (error) => {
            // no trading window means the request never left the browser and
            // the prompt to open one is already on screen. Nothing failed, so
            // this is a neutral note about what did not change, not a red
            // "did not complete" the user has to interpret.
            if (isTradingAuthorityRequiredError(error)) {
                toast.info(
                    'Stop-loss / take-profit unchanged. Start a trading window to edit it.',
                );
                return;
            }
            toastError(error, {
                operation: 'Stop-loss / take-profit update',
                duration: UPDATE_TRIGGERS_ERROR_TOAST_MS,
            });
        },
    });

    return { mutate, isPending };
}
